import React, { memo, useRef } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useVirtualizer } from '@tanstack/react-virtual';
import { User } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { safeArray } from '@/components/safeRender';
import { formatCurrency } from '@/lib/formatters';
import { SortableCard } from './SortableCard';
import { kanbanColumnChrome } from '@/lib/uiClasses';

const CARD_GAP = 12;
const CARD_ESTIMATE_HEIGHT = 280;

export interface DroppableColumnProps {
  etapa: { id: string; nome: string; descricao?: string; cor: string };
  cards: any[];
  selectedCards: Set<string>;
  onSelectAll: (etapaId: string, selectAll: boolean) => void;
  onCardSelect: (cardId: string, selected: boolean) => void;
  onCardClick: (card: any) => void;
}

const DroppableColumnInner: React.FC<DroppableColumnProps> = ({
  etapa,
  cards,
  selectedCards,
  onSelectAll,
  onCardSelect,
  onCardClick,
}) => {
  const { setNodeRef } = useDroppable({
    id: `etapa-${etapa.id}`,
  });

  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: cards.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => CARD_ESTIMATE_HEIGHT,
    gap: CARD_GAP,
    overscan: 4,
  });

  const valorTotalColuna = cards.reduce((total: number, card: any) => {
    const titulos = safeArray(card.titulos);
    return total + titulos.reduce((sum: number, titulo: any) => sum + Number(titulo.saldo || titulo.valor), 0);
  }, 0);

  const selectedInEtapa = cards.filter((card) => selectedCards.has(card.id));
  const allSelected = cards.length > 0 && selectedInEtapa.length === cards.length;
  const someSelected = selectedInEtapa.length > 0 && selectedInEtapa.length < cards.length;

  const handleSelectAllClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectAll(etapa.id, !allSelected);
  };

  return (
    <div ref={setNodeRef} className="w-56 lg:w-67 xl:w-[294px] flex-shrink-0 h-full flex flex-col">
      <div className={`rounded-t-lg p-4 border-b flex-shrink-0 ${kanbanColumnChrome} border-border`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <div onClick={handleSelectAllClick} className="cursor-pointer">
              <Checkbox checked={allSelected} onChange={() => {}} className={someSelected ? 'opacity-75' : ''} />
            </div>
            <div
              className="w-3 h-3 rounded-full border-2 border-white shadow-sm"
              style={{ backgroundColor: etapa.cor }}
            />
            <h3 className="font-semibold text-foreground">{etapa.nome}</h3>
          </div>
          <div className="flex flex-col items-end space-y-1">
            <Badge variant="secondary" className="text-xs font-medium">
              {cards.length}
            </Badge>
            <div className="text-xs font-medium text-muted-foreground">
              {formatCurrency(valorTotalColuna)}
            </div>
          </div>
        </div>
        {etapa.descricao && <p className="text-sm text-muted-foreground">{etapa.descricao}</p>}
      </div>

      <div className={`rounded-b-lg p-3 flex-1 overflow-hidden ${kanbanColumnChrome}`}>
        <SortableContext items={cards.map((card: any) => card.id)} strategy={verticalListSortingStrategy}>
          <div ref={scrollRef} className="h-full overflow-y-auto">
            {cards.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="w-12 h-12 mx-auto mb-3 bg-muted rounded-full flex items-center justify-center">
                  <User className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium">Nenhum cliente</p>
                <p className="text-xs">Arraste um card para esta coluna</p>
              </div>
            ) : (
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const card = cards[virtualRow.index];
                  return (
                    <div
                      key={card.id}
                      data-index={virtualRow.index}
                      ref={virtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <SortableCard
                        card={card}
                        isSelected={selectedCards.has(card.id)}
                        onSelect={onCardSelect}
                        onCardClick={onCardClick}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
};

function columnEqual(prev: DroppableColumnProps, next: DroppableColumnProps): boolean {
  if (prev.etapa.id !== next.etapa.id || prev.cards.length !== next.cards.length) {
    return false;
  }
  if (prev.onSelectAll !== next.onSelectAll || prev.onCardSelect !== next.onCardSelect) {
    return false;
  }
  if (prev.onCardClick !== next.onCardClick) {
    return false;
  }
  for (let i = 0; i < prev.cards.length; i++) {
    const a = prev.cards[i];
    const b = next.cards[i];
    if (!b || a.id !== b.id) return false;
    if (prev.selectedCards.has(a.id) !== next.selectedCards.has(b.id)) return false;
    if (a.gestorActionStatus !== b.gestorActionStatus) return false;
    if (a.valorTotal !== b.valorTotal || a.valorTotalVencido !== b.valorTotalVencido) return false;
    if (a.titulosEmAberto !== b.titulosEmAberto || a.titulosVencidos !== b.titulosVencidos) return false;
    const opsA = (a.titulos || []).map((t: any) => t.operadorId || t.operador?.id).join(',');
    const opsB = (b.titulos || []).map((t: any) => t.operadorId || t.operador?.id).join(',');
    if (opsA !== opsB) return false;
    if ((a.titulos || []).length !== (b.titulos || []).length) return false;
  }
  return true;
}

export const DroppableColumn = memo(DroppableColumnInner, columnEqual);
